import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface Location {
  id: string;
  countryCode: string;
  country?: string;
  worldRegion?: string;
  city?: string;
  admin1?: string;
  postalCode?: string;
  lat: number;
  lon: number;
  locationText?: string;
}

export interface LocationSearchResult {
  locations: Location[];
  count: number;
}

export interface NearbyLocationsResult extends LocationSearchResult {
  radius: number;
  referenceLocation: {
    id: string;
    lat: number;
    lon: number;
  };
}

/**
 * Search locations by text
 */
export const searchLocations = createAsyncThunk(
  'location/search',
  async ({ query, limit = 20 }: { query: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await api.get<LocationSearchResult>('/location/search', {
        params: { q: query, limit },
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to search locations';
      return rejectWithValue(message);
    }
  }
);

/**
 * Get location by ID
 */
export const getLocationById = createAsyncThunk(
  'location/getById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get<{ location: Location }>(`/location/${id}`);
      return response.data.location;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch location';
      return rejectWithValue(message);
    }
  }
);

/**
 * Get locations within radius
 */
export const getLocationsNearby = createAsyncThunk(
  'location/nearby',
  async (
    { locationId, radius = 25, limit = 50 }: { locationId: string; radius?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get<NearbyLocationsResult>(`/location/${locationId}/nearby`, {
        params: { radius, limit },
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch nearby locations';
      return rejectWithValue(message);
    }
  }
);

/**
 * Update user's home location
 */
export const updateHomeLocation = createAsyncThunk(
  'location/updateHome',
  async (homeId: string, { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', { homeId });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update home location';
      return rejectWithValue(message);
    }
  }
);

/**
 * Update user's touring location
 */
export const updateTouringLocation = createAsyncThunk(
  'location/updateTouring',
  async (touringId: string | null, { rejectWithValue }) => {
    try {
      const response = await api.patch('/user/preferences', { touringId });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update touring location';
      return rejectWithValue(message);
    }
  }
);
